import forEach from 'lodash/forEach'
import mapValues from 'lodash/mapValues'
import noop from 'lodash/noop'
import reduce from 'lodash/reduce'
import size from 'lodash/size'
import { useRouter } from 'next/compat/router'
import { useQueryStates } from 'nuqs'
import React, { createContext, FC, useMemo } from 'react'
import useSWR from 'swr'

import { Looks } from '@Components/Looks'
import { BlockTypeWithGlobal, LookTile, ProductListBlockFacetType, ProductListBlockFragmentFragment } from '@Generated/graphql'
import { algoliaFetcher } from '@Globals/Algolia/Fetcher'
import { createQueryStatesKeys, parseValidQueryParameters } from '@Globals/Algolia/Shared'
import {
  AlgoliaProductTile,
  BlockConfigAlgolia,
  FacetDynamicConfig,
  FacetStats,
  NumericFilter,
  Options,
  PageProductListBlockGuidedFilter,
  PageProductListBlockInjection,
  ProductSorting,
} from '@Globals/Algolia/Types'
import { algoliaProductToProductTile } from '@Globals/CMS/ProductHelpers'
import { useBlockComponents } from '@Globals/CMS/useBlockComponents'
import { useTranslation } from '@Globals/Translations'

export interface ProductSearchStaticContextType {
  facetConfig: ProductListBlockFragmentFragment['facetConfig']
  initialFacetStats: FacetStats
  injections: PageProductListBlockInjection[]
  usePagination: boolean
}

export const ProductSearchStaticContext = createContext<ProductSearchStaticContextType>({
  facetConfig: [],
  initialFacetStats: {},
  injections: [],
  usePagination: false,
})

export interface ProductSearchContextType {
  count: number
  currentHits: number
  facetDynamicConfig: Record<string, FacetDynamicConfig>
  guidedFilters: PageProductListBlockGuidedFilter[] | null
  hasActiveFilter: boolean
  hits: Record<string, AlgoliaProductTile>
  hitsArray: AlgoliaProductTile[]
  numericFilters: Record<string, NumericFilter>
  objectIDs: string[]
  page: number | null
  pages: number
  sorting: ProductSorting
  title: {
    base: string
    canonical?: string
    page?: string
  }

  resetFilters(): void
}

export const ProductSearchContext = createContext<ProductSearchContextType>({
  count: 0,
  currentHits: 0,
  objectIDs: [],
  facetDynamicConfig: {},
  guidedFilters: [],
  hasActiveFilter: false,
  hits: {},
  hitsArray: [],
  numericFilters: {},
  page: 1,
  pages: 0,
  sorting: ProductSorting.Default,
  title: {
    base: '',
  },

  resetFilters: noop,
})

export interface ProductSearchProviderProps {
  algoliaConfig?: BlockConfigAlgolia
  boosts?: string[]
  children?: React.ReactNode
  extraBlocks?: BlockTypeWithGlobal[]
  extraBlocksIDPrefix?: string
  facetConfig: ProductListBlockFragmentFragment['facetConfig']
  facets: string[]
  filters: string
  guidedFilters: PageProductListBlockGuidedFilter[] | null
  hitsPerPage: string
  injections: PageProductListBlockInjection[] | null
  looks?: LookTile[]
  title: string
}

// keep to 10 boosts max
const activeBoostIndexToHitIndex = [
  1,
  3,
  8,
  9,
  16,
  17,
  26,
  27,
  32,
  33,
]

export const ProductSearchProvider: FC<ProductSearchProviderProps> = ({
  algoliaConfig,
  boosts = [],
  children,
  extraBlocks: extraBlocksBase = [],
  extraBlocksIDPrefix,
  facetConfig,
  facets,
  filters,
  guidedFilters,
  hitsPerPage,
  injections = [],
  looks = [],
  title,
}) => {
  const router = useRouter()
  const {
    locale = '',
    query,
  } = router || {}
  const { t } = useTranslation()
  const usePagination = Boolean(algoliaConfig?.usePagination)

  const queryStatesKeysMap = useMemo(() => createQueryStatesKeys(facetConfig), [facetConfig])
  const [queryStates, setQueryStates] = useQueryStates(queryStatesKeysMap)

  const queryStatesCount = useMemo(() => reduce(
    queryStates,
    (memo, item, key) => {
      if (!item || !key.startsWith('filter_')) {
        return memo
      }

      if (Array.isArray(item)) {
        return memo + item.length
      }

      return memo + 1
    },
    0,
  ), [queryStates])

  const options = useMemo(() => {
    if (!router) {
      return {} as Options
    }

    const result: Options = {
      facets,
      facetFilters: [],
      filters,
      numericFilters: {},
      hitsPerPage,
      locale,
      optionalFilters: algoliaConfig?.optionalFilters || [],
      page: null,
      pagination: usePagination,
      sorting: ProductSorting.Default,
    }

    if (algoliaConfig?.useQueryParams) {
      Object.assign(result, parseValidQueryParameters(router!.query, facetConfig, algoliaConfig!.initialFacetStats))
      if (!algoliaConfig!.usePagination) {
        result.page = null
      }
    }

    return result
  }, [algoliaConfig, facets, facetConfig, filters, hitsPerPage, locale, router, usePagination])

  const swr = useSWR(
    options,
    algoliaFetcher,
    {
      keepPreviousData: true,
      revalidateOnMount: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  )

  const hitsArray = useMemo(() => {
    if (!usePagination || !swr.data?.hits || swr.data.hits.length === 0) {
      return []
    }

    if (boosts.length === 0) {
      return swr.data.hits.map(hit => algoliaProductToProductTile(hit))
    }

    const boostsSet = new Set(boosts)
    const activeBoostIDs = new Set<string>()
    const hits: AlgoliaProductTile[] = []
    const boostedHits = new Map<string, AlgoliaProductTile>()

    swr.data.hits.forEach((hit) => {
      if (boostsSet.has(hit.objectID)) {
        activeBoostIDs.add(hit.objectID)
        boostedHits.set(hit.objectID, algoliaProductToProductTile(hit, true))
        return
      }

      hits.push(algoliaProductToProductTile(hit))
    })

    let boostIndex = 0

    boosts.forEach((boostID) => {
      if (!activeBoostIDs.has(boostID)) {
        return
      }

      const targetIndex = activeBoostIndexToHitIndex[boostIndex]
      if (typeof targetIndex !== 'number') {
        return
      }

      const boostedHit = boostedHits.get(boostID)
      if (!boostedHit) {
        return
      }

      boostedHits.delete(boostID)
      boostIndex += 1

      if (hits.length <= targetIndex) {
        hits.push(boostedHit)
        return
      }

      hits.splice(targetIndex, 0, boostedHit)
    })

    return hits
  }, [boosts, swr.data?.hits, usePagination])

  const facetDynamicConfig = useMemo(
    () => reduce(facetConfig, (memo, facet) => { /* eslint-disable no-param-reassign */
      if (facet.type === ProductListBlockFacetType.EnumerableDefault
        || facet.type === ProductListBlockFacetType.EnumerableBoolean
        || facet.type === ProductListBlockFacetType.EnumerableColor
        || facet.type === ProductListBlockFacetType.EnumerableNumeric
      ) {
        let checkedCount = 0
        let groupCount = 0

        forEach(facet.values, (value) => {
          const checked = options.facetFilters.includes(`${facet.id}:${value.id}`)
          const count = swr?.data?.facets?.[facet.id!]?.[value.id!] || 0

          if (checked) {
            checkedCount += 1
          }

          memo[`${facet.parameter}:${value.id}`] = {
            checked,
            count,
            disabled: count === 0,
            key: value.key || '',
            name: value.name || null,
            type: 'value',
          }
        })

        forEach(facet.groups, (group) => {
          forEach(group.values, (value) => {
            const checked = options.facetFilters.includes(`${facet.id}:${value.id}`)
            const count = swr?.data?.facets?.[facet.id!]?.[value.id!] || 0

            if (checked) {
              checkedCount += 1
            }

            memo[`${facet.parameter}:${value.id}`] = {
              checked,
              count,
              disabled: count === 0,
              key: value.key || '',
              name: value.name || null,
              type: 'value',
            }
          })

          groupCount += 1
        })

        memo[facet.parameter!] = {
          count: checkedCount,
          disabled: false,
          name: facet.name || null,
          showGroupTitle: groupCount > 1 && facet.key !== 'colorShades',
          type: 'facet',
        }
      }

      if (facet.type === ProductListBlockFacetType.RangeDefault || facet.type === ProductListBlockFacetType.RangePrice) {
        const numericFilter = options.numericFilters?.[facet.id!]
        memo[facet.parameter!] = {
          count: numericFilter ? 1 : 0,
          disabled: !!swr?.data?.facetsStats?.[facet.id!],
          name: facet.name || null,
          showGroupTitle: false,
          type: 'facet',
        }
      }

      return memo
    }, {} as Record<string, FacetDynamicConfig>),
    [
      facetConfig,
      options.facetFilters,
      options.numericFilters,
      swr?.data?.facets,
      swr?.data?.facetsStats,
    ],
  )

  const titleSegments = useMemo(() => {
    const segments: ProductSearchContextType['title'] = {
      base: title,
    }

    if (!query) {
      return segments
    }

    if (!usePagination) {
      return segments
    }

    if (!algoliaConfig?.canonicals) {
      return segments
    }

    // if (query.page && typeof query.page === 'string') {
    //   const pageNumber = parseInt(query.page, 10)
    //   if (pageNumber > 1) {
    //     segments.page = t('common:PAGINATION_PAGE_HEADER', { count: pageNumber })
    //   }
    // }

    const incomingFilters = Object.keys(query).filter(key => key.startsWith('filter_'))
    if (incomingFilters.length === 1) {
      const filterKey = incomingFilters[0]
      const filterValue = query[filterKey]

      if (filterValue && typeof filterValue === 'string') {
        const canonical = algoliaConfig.canonicals[`${filterKey}=${filterValue}`]
        if (canonical?.name?.defaults) {
          segments.canonical = t(canonical.name.defaults)
        }
      }
    }

    return segments
  }, [algoliaConfig?.canonicals, query, t, title, usePagination])

  const valueStatic = useMemo(() => ({
    facetConfig,
    initialFacetStats: algoliaConfig?.initialFacetStats || {},
    injections: injections || [],
    usePagination,
  }), [facetConfig, algoliaConfig?.initialFacetStats, injections, usePagination])

  const value = useMemo(
    (): ProductSearchContextType => ({
      count: queryStatesCount,
      currentHits: swr.data?.totalHits || 0,
      objectIDs: swr.data?.objectIDs ?? [],
      facetDynamicConfig,
      guidedFilters,
      hasActiveFilter: size(options.facetFilters) !== 0 || size(options.numericFilters) !== 0,
      hits: usePagination ? {} : (algoliaConfig?.hits || {}),
      hitsArray,
      numericFilters: options.numericFilters,
      page: options.page,
      pages: swr.data?.pages || 0,
      sorting: options.sorting,
      title: titleSegments,

      resetFilters() {
        setQueryStates(prev => ({
          ...mapValues(prev, () => null),
          sorting: prev.sorting,
        }))
      },
    }),
    [
      algoliaConfig?.hits,
      facetDynamicConfig,
      guidedFilters,
      hitsArray,
      options.facetFilters,
      options.numericFilters,
      options.page,
      options.sorting,
      queryStatesCount,
      setQueryStates,
      swr.data?.objectIDs,
      swr.data?.totalHits,
      swr.data?.pages,
      titleSegments,
      usePagination,
    ],
  )

  const [extraBlocks, showExtraBlocks] = useMemo(() => {
    if (!usePagination) {
      return [extraBlocksBase, !value.hasActiveFilter]
    }

    // if (usePagination && (!options.page || options.page === 1)) {
    //   showExtraBlocks = true
    // }

    return [extraBlocksBase, true]
  }, [extraBlocksBase, usePagination, value.hasActiveFilter])

  const extraBlocksComponents = useBlockComponents(extraBlocks, `${extraBlocksIDPrefix}-extra-block`)

  return (
    <ProductSearchStaticContext.Provider value={valueStatic}>
      <ProductSearchContext.Provider value={value}>
        {children}
        {Boolean(looks) && looks.length !== 0 && (
          <Looks
            looks={looks}
            settings={{
              cardAspectRatio: 'portrait',
              cardSize: 'medium',
              cardVariant: 'background',
              enableSlider: true,
              rowsDesktop: 'auto',
              rowsMobile: 'auto',
            }}
            title={t('common:GET_THE_LOOK')}
          />
        )}
        {showExtraBlocks ? extraBlocksComponents : null}
      </ProductSearchContext.Provider>
    </ProductSearchStaticContext.Provider>
  )
}
